<script setup>
// ============================================================================
// BonBoZ: quản lý danh sách chủ đề mẫu trả lời.
//
// Danh sách DÙNG CHUNG: đổi tên hay màu ở đây là mọi mẫu mang chủ đề đó đổi
// theo. Vì thế không có ô gõ chủ đề tự do ở màn tạo mẫu — mẫu chỉ chọn từ danh
// sách này.
//
// XOÁ chủ đề KHÔNG xoá mẫu: mẫu chỉ mất chủ đề (`on_delete: :nullify` trong
// migration 20260916000002). Hộp xác nhận nói rõ số mẫu bị ảnh hưởng.
// ============================================================================
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ChuDeMauAPI from 'dashboard/api/bonboz/chuDeMau';
import {
  dsChuDe,
  napChuDe,
  MAU_SAN,
  mauChip,
} from 'dashboard/helper/bonboz/chuDeMau';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['close']);

const { t } = useI18n();
const { isAdmin } = useAdmin();

const tenMoi = ref('');
const mauMoi = ref(MAU_SAN[0]);
const dangLuu = ref(false);
const suaId = ref(null);
const hopXoa = ref(null);
const sapXoa = ref(null);

napChuDe(true);

const dungLoi = e => e?.response?.data?.error || t('BONBOZ.CHU_DE.LOI');

const them = async () => {
  const ten = tenMoi.value.trim();
  if (!ten || dangLuu.value) return;
  dangLuu.value = true;
  try {
    await ChuDeMauAPI.taoMoi({ ten, mau: mauMoi.value });
    await napChuDe(true);
    tenMoi.value = '';
  } catch (e) {
    useAlert(dungLoi(e));
  } finally {
    dangLuu.value = false;
  }
};

// Sua tai cho: bam vao chu de nao thi o do thanh o nhap, khong mo them hop.
const luuSua = async cd => {
  const ten = cd.ten.trim();
  if (!ten) return;
  try {
    await ChuDeMauAPI.sua(cd.id, { ten, mau: cd.mau });
    await napChuDe(true);
  } catch (e) {
    useAlert(dungLoi(e));
    await napChuDe(true); // tra ve gia tri that sau khi sua hong
  } finally {
    suaId.value = null;
  }
};

const hoiXoa = cd => {
  sapXoa.value = cd;
  hopXoa.value?.open();
};

const xoa = async () => {
  try {
    await ChuDeMauAPI.xoa(sapXoa.value.id);
    await napChuDe(true);
  } catch (e) {
    useAlert(dungLoi(e));
  } finally {
    sapXoa.value = null;
  }
};
</script>

<template>
  <woot-modal show :on-close="() => emit('close')">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('BONBOZ.CHU_DE.TIEU_DE')"
        :header-content="$t('BONBOZ.CHU_DE.MO_TA')"
      />
      <div class="flex flex-col gap-4 p-8 pt-4">
        <div v-if="isAdmin" class="flex items-end gap-2">
          <label class="flex-1 !mb-0">
            {{ $t('BONBOZ.CHU_DE.TEN') }}
            <input
              v-model="tenMoi"
              type="text"
              class="!mb-0"
              :placeholder="$t('BONBOZ.CHU_DE.TEN_VD')"
              @keyup.enter="them"
            />
          </label>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ $t('BONBOZ.CHU_DE.MAU') }}
            </span>
            <div class="flex items-center gap-1 h-[38px]">
              <button
                v-for="m in MAU_SAN"
                :key="m"
                type="button"
                class="rounded-md size-5 border-2"
                :style="{
                  backgroundColor: m,
                  borderColor:
                    m === mauMoi ? 'rgb(var(--slate-12))' : 'transparent',
                }"
                :aria-label="m"
                @click="mauMoi = m"
              />
              <!-- O chon mau tu do ngay canh bo mau san: ai can dung mau nhan
                   dien rieng thi khong bi chan, con lai cu bam mot trong 8. -->
              <input
                v-model="mauMoi"
                type="color"
                class="w-6 h-6 p-0 !mb-0 bg-transparent border-0 cursor-pointer"
                :title="$t('BONBOZ.CHU_DE.MAU_TU_DO')"
              />
            </div>
          </div>
          <NextButton
            sm
            :label="$t('BONBOZ.CHU_DE.THEM')"
            :disabled="!tenMoi.trim() || dangLuu"
            :is-loading="dangLuu"
            class="mb-0.5"
            @click="them"
          />
        </div>

        <div
          v-if="!dsChuDe.length"
          class="py-6 text-sm text-center text-n-slate-11"
        >
          {{ $t('BONBOZ.CHU_DE.TRONG') }}
        </div>
        <div v-else class="flex flex-col border rounded-lg border-n-weak">
          <div
            v-for="cd in dsChuDe"
            :key="cd.id"
            class="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 border-n-weak"
          >
            <input
              v-if="suaId === cd.id"
              v-model="cd.mau"
              type="color"
              class="w-6 h-6 p-0 !mb-0 bg-transparent border-0 cursor-pointer shrink-0"
            />
            <span
              v-else
              class="rounded-full size-3 shrink-0"
              :style="{ backgroundColor: cd.mau }"
            />
            <input
              v-if="suaId === cd.id"
              v-model="cd.ten"
              type="text"
              class="flex-1 !mb-0 !h-8 text-sm"
              @keyup.enter="luuSua(cd)"
            />
            <span
              v-else
              class="flex-1 px-2 py-0.5 text-sm rounded-full truncate max-w-fit"
              :style="mauChip(cd.mau)"
            >
              {{ cd.ten }}
            </span>
            <span class="text-xs text-n-slate-11 shrink-0">
              {{ $t('BONBOZ.CHU_DE.SO_MAU', { so: cd.so_mau }) }}
            </span>
            <template v-if="isAdmin">
              <NextButton
                v-if="suaId === cd.id"
                xs
                slate
                faded
                icon="i-lucide-check"
                @click="luuSua(cd)"
              />
              <NextButton
                v-else
                xs
                slate
                ghost
                icon="i-lucide-pencil"
                @click="suaId = cd.id"
              />
              <NextButton
                xs
                ruby
                ghost
                icon="i-lucide-trash-2"
                @click="hoiXoa(cd)"
              />
            </template>
          </div>
        </div>
      </div>
    </div>

    <Dialog
      ref="hopXoa"
      type="alert"
      :title="$t('BONBOZ.CHU_DE.XOA')"
      :description="
        $t('BONBOZ.CHU_DE.HOI_XOA', {
          ten: sapXoa?.ten,
          so: sapXoa?.so_mau || 0,
        })
      "
      :confirm-button-label="$t('BONBOZ.CHU_DE.XAC_NHAN_XOA')"
      @confirm="xoa"
    />
  </woot-modal>
</template>
